import { useContext, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { useSelector } from '../../selectors/useSelector';

import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import { useGetPropertyConditionsSummary } from '../../hooks/queries/propertycondition';

import PropertyConditionModule from './components/PropertyConditionModule';
import EmptyPropertyConditionModule from './components/EmptyPropertyConditionModule';
import PropertyConditionErrorModule from './components/PropertyConditionErrorModule';
import { updateLoadingState } from '../../providers/LoadingMapContext/actions';

const ConnectedPropertyConditionModule = () => {
  const { listingAlias } = useSelector(
    (state) => ({
      listingAlias: state.properties.currentProperty.listingAlias,
    }),
    shallowEqual,
  );

  const {
    reducerDispatch,
    loadingByComponent: { ConnectedPropertyCondition: isConnectedPropertyConditionModuleLoading },
  } = useContext(LoadingMapContext);

  const { data, isPending, error } = useGetPropertyConditionsSummary(listingAlias);

  const { title = '', actions = [], description = '', propertyConditions = [] } = data || {};

  useEffect(() => {
    updateLoadingState({
      reducerDispatch,
      data: {
        propertyConditionSummary: isPending,
      },
    });
  }, [isPending]);

  const currentReport = useMemo(
    () =>
      propertyConditions?.filter((propertyCondition: { isArchived: boolean }) => !propertyCondition.isArchived)?.[0],
    [propertyConditions],
  );

  if (error || ((actions == null || !actions.length) && !isConnectedPropertyConditionModuleLoading)) {
    return (
      <PropertyConditionErrorModule
        isUnsupportedStateError={error?.error?.data?.messageCode === 'unsupportedState'}
        partialErrorMessages={error?.error?.data?.partialMessages || [{ text: 'Sorry, an error occurred.' }]}
      />
    );
  }

  return currentReport || isConnectedPropertyConditionModuleLoading ? (
    <PropertyConditionModule
      title={title || 'Property condition report'}
      description={description}
      actionUrl={actions[0]?.url}
      actionText={actions[0]?.text}
      propertyConditionReport={currentReport}
      listingAlias={listingAlias}
    />
  ) : (
    <EmptyPropertyConditionModule
      title={title}
      description={description}
      actionUrl={actions[0].url}
      actionText={actions[0].text}
    />
  );
};

export default ConnectedPropertyConditionModule;
